import { Action, Dispatch, ThunkAction } from "@reduxjs/toolkit";
import productLister from "../../assets/userRoles/productLister.json";
import superAdmin from "../../assets/userRoles/superAdmin.json";
import customer from "../../assets/userRoles/customer.json";
import { setError, setFilteredMenu, setRoleConfig } from "./reducer";
import { menuData } from "Layout/LayoutMenuData";
import { RootState } from "slices";

type SubItem = string | { id: string; subItems: string[] };

type Extension = {
  id: string; // e.g., "diamond", "jewellery"
  subItems: string | SubItem[]; // e.g., ["shapes", "prices"]
};

const getRoleConfiguration = async (role: string) => {

  const localConfigs: Record<
    string,
    { role: string; extensions: Extension[] }
  > = {
    productsLister: {
      role: productLister.role,
      extensions: productLister.extensions || [],
    },
    superAdmin: {
      role: superAdmin.role,
      extensions: superAdmin.extensions || [], // Fallback to empty array if extensions are missing
    },
    customer: {
      role: customer.role,
      extensions: customer.extensions || [], // Fallback to empty array if extensions are missing
    }
    };
    if (!localConfigs[role]) {
      console.warn(`No local configuration found for role: ${role}`);
      return { role, extensions: [] }; // Fallback
    }
    
    localStorage.setItem(
      "extensions",
      JSON.stringify(localConfigs[role].extensions)
    );
    return {
      role: localConfigs[role].role,
      extensions: localConfigs[role].extensions || [],
    };
  };

// Filter menu based on role configuration
const filterMenuItemsByRole = (
  menu: any[],
  roleConfig: { extensions: Extension[] }
) => {
  return menu
    .map((menuItem) => {
      const extension = roleConfig.extensions.find(
        (ext) => ext.id === menuItem.id
      );

      if (!extension) {
        // If no match, skip this menu item
        return null;
      }

      const filteredSubItems = menuItem.subItems?.filter((subItem: any) =>
        extension.subItems.includes(subItem.id)
      );

      return {
        ...menuItem,
        subItems: filteredSubItems,
      };
    })
    .filter(Boolean); // Remove null values
};

export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  unknown,
  Action<any>
>;

export const loadRoleConfig =
  (role: string, history: any): AppThunk =>
  async (dispatch: Dispatch) => {
    try {
      // If filteredMenu is empty, fetch configuration
      const response = await getRoleConfiguration(role);

      if (!response.extensions || response.extensions.length === 0) {
        // Dispatch logout or redirect action (depending on your implementation)
        history("/access-denied"); // This would remove user session, or you can dispatch another action to redirect

        return; // Exit early since the user is not authorized
      }

      // Filter menu based on role configuration
      const config = {
        role: response.role,
        extensions: response.extensions || [],
      };

      dispatch(setRoleConfig(config));
      const newFilteredMenu = filterMenuItemsByRole(menuData, config);

      if (newFilteredMenu.length === 0) {
        dispatch(setFilteredMenu(menuData)); // Fallback to full menu
      } else {
        dispatch(setFilteredMenu(newFilteredMenu));
      };
    } catch (error: any) {
      console.error("Failed to load role config:", error);
      dispatch(setError(error.message));
    }
  };
